import clsx from "clsx";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { MouseEvent, useEffect } from "react";
import { NodeApi, NodeRendererProps } from "react-arborist";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";
import { ResursTreeApi } from "../../../services/modules/tree";
import {
  selectNodes,
  setLastTargetDrop,
  toggleNode,
} from "../../../services/slices/tree";
import { RootState, useAppDispatch } from "../../../services/types/store-types";
import { ResursIcon } from "../../icons/icon";
import { TResursTreeNode } from "../treeTypes";
import { Indent } from "./Indent";
import { StateIcon } from "./icons-state/StateIcon";
import { Arrow } from "./node-arrow/node-arrow";
import styles from "./node.module.css";

const toggleDirectChildren = (
  node: NodeApi<TResursTreeNode>,
  open: boolean
): void => {
  if (node.children) {
    node.children.forEach((child) => {
      if (open) {
        child.open();
      } else {
        child.close();
      }
    });
  }
};

const debouncedHover = debounce(
  (
    item,
    node: NodeApi<TResursTreeNode>,
    selectedNodes: {
      tree: string | null;
      objectIds: string[];
      nodes: TResursTreeNode[];
    },
    dispatch,
    lastTargetDrop: string
  ) => {
    const treeSource = selectedNodes.tree;
    const treeDest = String(node.data.tree);
    const idSource =
      selectedNodes.objectIds.length > 1 ? selectedNodes.objectIds : [item.id];
    const idDest = String(node.data.id);
    dispatch(setLastTargetDrop(idDest));
    if (!treeSource) return;
    if (lastTargetDrop === idDest) return;
    dispatch(
      ResursTreeApi.canCreateTreeLink({
        treeSource: treeSource,
        treeDest: treeDest,
        idSource: idSource,
        idDest: idDest,
      })
    );
  },
  50
);

export const Node = ({
  node,
  style,
  dragHandle,
  setVisibleConfirmDelete,
}: NodeRendererProps<TResursTreeNode> & {
  setVisibleConfirmDelete: (arg: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const canCreateNode = useSelector(
    (state: RootState) => state.tree.canCreateTreeLink
  );
  const selectedNodes = useSelector(
    (state: RootState) => state.tree.selectedNodes
  );

  const { lastTargetDrop } = useSelector((state: RootState) => state.tree);

  // Обработчик кликов для стрелки
  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем всплытие до основного узла
    const isRootNode = String(node.data.id).includes("root");

    if (isRootNode) {
      const firstChild = node.children?.[0];
      const shouldOpen = firstChild ? !firstChild.isOpen : true;
      toggleDirectChildren(node, shouldOpen);
      return;
    }

    if (node.data.has_children && !node.data.children?.length) {
      const id = node.data.id;
      const objId = [id];
      const toggle = () => node.open();
      dispatch(
        ResursTreeApi.getTree(node.data.tree)({ objectId: objId, toggle })
      );
    }
    node.toggle();

    // Обновляем состояние в Redux с уникальным ключом tree + nodeId
    dispatch(toggleNode({ tree: node.data.tree, nodeId: node.id }));
  };

  const [, drop] = useDrop({
    accept: "NODE",
    drop(item: any) {
      if (canCreateNode) {
        const treeSource = selectedNodes.tree;
        const treeDest = node.data.tree;
        const idSource =
          selectedNodes.objectIds.length > 1
            ? selectedNodes.objectIds
            : [item.id];
        const idDest = String(node.data.id);

        if (!treeSource) return;

        ResursTreeApi.createTreeLink({
          treeSource: treeSource,
          treeDest: treeDest,
          idSource: idSource,
          idDest: idDest,
        });
      }
    },
    //canDrop: () => canCreateNode,
    hover: (item) => {
      debouncedHover(item, node, selectedNodes, dispatch, lastTargetDrop);
    },
  });

  const handleContextMenu = (e: any, node: NodeApi<TResursTreeNode>) => {
    e.preventDefault();

    // Проверяем, изменилось ли дерево или выбрана одна нода
    const isTreeChanged = selectedNodes.tree !== node.data.tree;
    const isSingleSelect = selectedNodes.objectIds.length < 2;

    if (isTreeChanged || isSingleSelect) {
      node.select();
      // ВАЖНО: обновляем lastSelect в Redux с правильным деревом,
      // иначе getContextMenuProps использует старое дерево для команд
      dispatch(
        selectNodes({
          tree: node.data.tree,
          objectIds: [node.id],
          nodes: [node.data],
        })
      );
    }

    getPropertys();
  };
  const getPropertys = () => {
    const objects = Array.from(node.tree.selectedIds);
    const tree = node.data.tree;
    dispatch(ResursTreeApi.getPropertysRedux({ objects: objects, tree: tree }));
  };

  const hasArrow =
    (node.data.has_children && node.data.tree !== "operator") ||
    (node.data.children && node.data.children.length > 0);

  // Обработчик выбора узла
  const handleSelect = (selected: NodeApi<TResursTreeNode>[]) => {
    if (!selected || selected.length === 0) return;
    const objectIds = selected.map((node) => node.id);

    dispatch(
      selectNodes({
        tree: node.data.tree,
        objectIds,
        nodes: selected.map((node) => node.data),
      })
    );
  };
  const handleSingleClick = (event: any) => {
    setTimeout(() => {
      handleSelect(node.tree.selectedNodes);
    }, 150);
  };

  const handleDoubleClick = (event: any) => {
    handleSelect([node]);
    const isRootNode = String(node.data.id).includes("root");

    if (isRootNode) {
      const firstChild = node.children?.[0];
      const shouldOpen = firstChild ? !firstChild.isOpen : true;
      toggleDirectChildren(node, shouldOpen);
      return;
    }

    if (node.data.has_children && !node.data.children?.length) {
      const id = node.data.id;
      const objId = [id];
      const toggle = () => node.open();
      dispatch(
        ResursTreeApi.getTree(node.data.tree)({ objectId: objId, toggle })
      );
    }
    node.toggle();

    // Обновляем состояние в Redux с уникальным ключом tree + nodeId
    dispatch(toggleNode({ tree: node.data.tree, nodeId: node.id }));
  };

  const handleClick = (e: MouseEvent) => {
    switch (e.detail) {
      case 1:
        handleSingleClick(e);
        break;
      case 2:
        handleDoubleClick(e);
        break;
      default:
        handleDoubleClick(e);
        break;
    }
  };

  const handleClickOutside = (event: any) => {
    try {
      if (event.target.className.includes("treeInstanse")) {
        dispatch(
          selectNodes({
            tree: node.data.tree,
            objectIds: [],
            nodes: [],
          })
        );
        // dispatch(
        //   selectNodes({
        //     tree: node.data.tree,
        //     objectIds: ["root"],
        //     nodes: [node.tree.firstNode.data],
        //   })
        // );
        // логика на случай если нужно по дефолту ставить root когда ничего не выбрано
      }
    } catch {}
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div
      data-at-wc-tree-node={node.data.tree}
      className={styles.nodeContainer}
      ref={(nodeElement) => {
        // Применяем drop и dragHandle к одному и тому же элементу
        drop(nodeElement); // Применяем drop
        if (dragHandle) {
          dragHandle(nodeElement); // Применяем dragHandle
        }
      }}
      id={String(node.data.id)}
      key={node.data.id}
      title={node.data.stateText}
      onContextMenu={(e) => handleContextMenu(e, node)}
      style={style}
      onMouseDown={handleClick}
    >
      <div className={clsx(styles.node, node.state)}>
        {/**
         * Блок отступа или стрелки
         */}
        {hasArrow ? (
          <Arrow node={node} handleClick={handleArrowClick} />
        ) : (
          <Indent indent={24} />
        )}
        {/**
         * Блок Иконки устройства или объекта
         */}
        <div
          style={{
            height: 24,
            width: 28,
            position: "relative",
          }}
        >
          <ResursIcon icon={node.data.icon || node.data.type} />
          {node.data.state !== null && node.data.state !== undefined && (
            <StateIcon state={node.data.state} />
          )}
        </div>

        {/**
         * Текст узла дерева
         */}
        <div
          className={styles.treeLabel}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          id={node.data.id}
        >
          {node.data.name}
        </div>

        {String(node.data.id).includes("root") ||
        node.data.tree === "home" ||
        node.data.tree === "config" ? (
          <></>
        ) : (
          <Button
            icon={"pi pi-trash"}
            rounded
            text
            severity="danger"
            aria-label="delete"
            className={styles.delete}
            onDoubleClick={(e) => {
              //Предотвращение всплытия события до верхнего уровня узла
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedNodes.objectIds.length === 0) {
                dispatch(
                  selectNodes({
                    nodes: [node.data],
                    objectIds: [node.data.id],
                    tree: node.data.tree,
                  })
                );
              }
              setVisibleConfirmDelete(true);
            }}
          />
        )}
      </div>
    </div>
  );
};
